/* Sixty8 Adventure — Arctic Grand Tourer motion layer for Horizon. v2
   Text engine: per-char / per-word staggered reveals (Framer-lab style).
   TEXT EFFECTS ALWAYS RUN (explicit client requirement).
   Large-surface motion (parallax, zoom, marquee) still honours
   prefers-reduced-motion. One rAF loop. Transform/opacity/filter only. */
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------- text splitting */
  /* Walks text nodes only, so nested spans (gold italics) survive. */
  function splitEl(el, mode) {
    if (el.__s68split) return;
    el.__s68split = true;
    /* Screen readers get the intact sentence; the animated spans are decoration */
    var plain = el.textContent.replace(/\s+/g, ' ').trim();
    if (plain) { el.setAttribute('aria-label', plain); el.setAttribute('role', 'text'); }
    var unitDelay = mode === 'chars' ? 0.022 : 0.05;
    var maxDelay = 0.9, idx = 0;

    function walk(node) {
      if (node.nodeType === 3) {
        var text = node.nodeValue;
        if (!text.trim()) return;
        var frag = document.createDocumentFragment();
        text.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          var w = document.createElement('span');
          w.className = 's68-w';
          w.setAttribute('aria-hidden', 'true');
          if (mode === 'chars') {
            for (var i = 0; i < part.length; i++) {
              var c = document.createElement('span');
              c.className = 's68-c';
              c.style.setProperty('--d', Math.min(idx * unitDelay, maxDelay).toFixed(3) + 's');
              c.textContent = part[i];
              w.appendChild(c); idx++;
            }
          } else {
            var cw = document.createElement('span');
            cw.className = 's68-c s68-c--word';
            cw.style.setProperty('--d', Math.min(idx * unitDelay, maxDelay).toFixed(3) + 's');
            cw.textContent = part;
            w.appendChild(cw); idx++;
          }
          frag.appendChild(w);
        });
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === 1 && node.tagName !== 'BR' &&
                 !/s68-(w|c)/.test(node.className || '')) {
        [].slice.call(node.childNodes).forEach(walk);
      }
    }
    [].slice.call(el.childNodes).forEach(walk);
  }

  function init(root) {
    root = root || document;

    /* External links (the magazine, socials) open in a new tab */
    root.querySelectorAll('a[href^="http"]').forEach(function (a) {
      if (a.hostname !== window.location.hostname) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
    });

    /* Headlines: hero h1 by character, other data-s68-lines by word */
    root.querySelectorAll('[data-s68-lines]').forEach(function (el) {
      splitEl(el, el.closest('.s68-hero') ? 'chars' : 'words');
    });
    /* Founder quote: word reveal with the full blur treatment */
    root.querySelectorAll('.s68-quote blockquote').forEach(function (el) {
      el.setAttribute('data-s68-lines', '');
      splitEl(el, 'words');
    });
    /* Leads and body intro text: word reveal, softer */
    root.querySelectorAll('.s68-hero__lead, .s68-lead').forEach(function (el) {
      el.classList.add('s68-soft');
      splitEl(el, 'words');
    });

    /* Reveal observer — ALWAYS on, text must animate for everyone */
    var revealables = root.querySelectorAll('[data-s68-lines], .s68-soft, .s68-up, .s68-zoom');
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('s68-in');
          io.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      revealables.forEach(function (el) { io.observe(el); });
    } else {
      revealables.forEach(function (el) { el.classList.add('s68-in'); });
    }

    /* Above the fold never waits on an observer */
    requestAnimationFrame(function () {
      root.querySelectorAll('.s68-hero, .s68-pagehero').forEach(function (h) {
        h.classList.add('s68-in');
        h.querySelectorAll('[data-s68-lines], .s68-soft, .s68-up').forEach(function (el) {
          el.classList.add('s68-in');
        });
      });
    });

    /* Counters */
    var counters = root.querySelectorAll('[data-s68-count]');
    var run = function (el) {
      var target = parseFloat(el.getAttribute('data-s68-count'));
      var t0 = null, dur = 1400;
      var step = function (t) {
        if (!t0) t0 = t;
        var p = Math.min((t - t0) / dur, 1);
        el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (counters.length && 'IntersectionObserver' in window) {
      var cio = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { run(e.target); cio.unobserve(e.target); } });
      }, { threshold: 0.5 });
      counters.forEach(function (el) { cio.observe(el); });
    } else {
      counters.forEach(run);
    }
  }

  /* Hero films carry no src in the markup: the poster paints instantly and
     the film is only fetched on desktop-width screens, after the page has
     loaded. Phones (and data-saver users) keep the poster — it is the LCP. */
  function lazyVideo() {
    var vids = document.querySelectorAll('video[data-s68-lazy-video]');
    if (!vids.length) return;
    var conn = navigator.connection;
    if (window.innerWidth < 768 || (conn && conn.saveData)) return;
    var start = function () {
      vids.forEach(function (v) {
        if (v.src) return;
        v.addEventListener('playing', function () { v.classList.add('s68-vid-on'); }, { once: true });
        v.src = v.getAttribute('data-s68-lazy-video');
        var p = v.play();
        /* a hidden tab rejects play(); try once more when it becomes visible */
        if (p && p.catch) p.catch(function () {
          var retry = function () {
            if (document.hidden) return;
            document.removeEventListener('visibilitychange', retry);
            var q = v.play();
            if (q && q.catch) q.catch(function () {});
          };
          document.addEventListener('visibilitychange', retry);
        });
      });
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  /* Light panels drift over the dark ground — ALWAYS runs (small travel,
     same design requirement as the text engine) */
  function floats() {
    if (!document.querySelector('[data-s68-float]')) return;
    var ticking = false;
    var frame = function () {
      var vh = window.innerHeight;
      /* read phase first, then write phase — interleaving the two forces
         a layout flush per element */
      var writes = [];
      document.querySelectorAll('[data-s68-float]').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -300 || r.top > vh + 300) return;
        var speed = parseFloat(el.getAttribute('data-s68-float')) || 0.08;
        var p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - vh / 2) / ((vh + r.height) / 2)));
        writes.push([el, (-p * speed * 260).toFixed(1) + 'px']);
      });
      writes.forEach(function (w) { w[0].style.setProperty('--s68-fy', w[1]); });
      ticking = false;
    };
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    frame();
  }

  /* Parallax + marquee lean — the only motion still gated by reduce */
  function motion() {
    if (reduce) return;
    var lastY = window.scrollY, vel = 0, ticking = false;
    var frame = function () {
      var vh = window.innerHeight, y = window.scrollY;
      var writes = [];
      document.querySelectorAll('[data-s68-par]').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var speed = parseFloat(el.getAttribute('data-s68-par')) || 0.16;
        var p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - vh / 2) / ((vh + r.height) / 2)));
        writes.push([el, (-p * speed * r.height * 0.5).toFixed(1) + 'px']);
      });
      writes.forEach(function (w) { w[0].style.setProperty('--s68-py', w[1]); });
      var mq = document.querySelector('.s68-marquee');
      if (mq) {
        vel += ((y - lastY) - vel) * 0.12;
        mq.style.setProperty('--s68-skew', Math.max(-6, Math.min(6, vel * 0.14)).toFixed(2) + 'deg');
      }
      lastY = y; ticking = false;
    };
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    frame();
  }

  /* YouTube facade: swap the thumbnail for the real player on demand */
  function ytFacade() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.s68-ytfacade');
      if (!btn) return;
      var id = btn.getAttribute('data-s68-yt');
      if (!id) return;
      var frame = document.createElement('iframe');
      frame.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1';
      frame.title = btn.getAttribute('data-s68-yt-title') || 'Sixty8 Adventure film';
      frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0';
      frame.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture';
      frame.allowFullscreen = true;
      btn.replaceWith(frame);
    });
  }

  /* Light / dark switch. The mode lives on <html data-s68-mode> (set before
     first paint in layout/theme.liquid); the choice is remembered per browser. */
  function modes() {
    var root = document.documentElement;
    var sync = function () {
      var light = root.getAttribute('data-s68-mode') === 'light';
      document.querySelectorAll('[data-s68-mode-toggle]').forEach(function (b) {
        b.setAttribute('aria-pressed', light ? 'true' : 'false');
        b.setAttribute('aria-label', light ? 'Switch to dark mode' : 'Switch to light mode');
      });
    };
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-s68-mode-toggle]');
      if (!btn) return;
      var next = root.getAttribute('data-s68-mode') === 'light' ? 'dark' : 'light';
      root.setAttribute('data-s68-mode', next);
      try { localStorage.setItem('s68-mode', next); } catch (err) {}
      sync();
    });
    sync();
    document.addEventListener('shopify:section:load', sync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); lazyVideo(); ytFacade(); floats(); motion(); modes(); });
  } else { init(); lazyVideo(); ytFacade(); floats(); motion(); modes(); }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
